/**
 * Footage the user attached or linked on the hero, and the upload of each after Send.
 * Uploads run from this store, not the hero component, so they keep going after the
 * hero navigates to the workspace.
 */
import { create } from 'zustand';
import { assetKind, assetLimitMessage, formatFileSize } from '@agentic-ivy/shared-types';
import { UploadError } from '../../../web-core/src/upload/multipart';
import { footageLinkLabel, type FootageLink, type FootageLinkKind } from '@/lib/footage/links';
import type { FootageUploadClient } from '@/lib/footage/uploadClient';

export type FootageItemStatus = 'staged' | 'uploading' | 'importing' | 'done' | 'error';

export interface FootageItem {
  id: string;
  name: string;
  status: FootageItemStatus;
  pct: number;
  file?: File;
  size?: number;
  url?: string;
  linkKind?: FootageLinkKind;
  sourceId?: string;
  error?: string;
}

interface State {
  items: FootageItem[];
  addFiles(files: File[]): void;
  syncLinks(links: FootageLink[]): void;
  remove(id: string): void;
  reset(): void;
  pruneFinished(): void;
  start(conversationId: string, client: FootageUploadClient): Promise<void>;
}

let seq = 0;
const nextId = () => `ft-${++seq}`;
const controllers = new Map<string, AbortController>();

function errorText(err: unknown, name: string): string {
  if (err instanceof UploadError && err.reason === 'network') return `The connection dropped while uploading ${name}. Attach it again to pick up where it stopped.`;
  if (err instanceof UploadError && err.reason === 'auth') return `Your sign-in expired while uploading ${name}. Sign in again, then attach it again.`;
  if (err instanceof UploadError && err.reason === 'gone') return `The upload of ${name} is no longer open. Attach it again to start over.`;
  return err instanceof Error ? err.message : `The upload of ${name} failed. Attach it again.`;
}

export const useFootageIntakeStore = create<State>((set, get) => {
  const patch = (id: string, p: Partial<FootageItem>) =>
    set((s) => ({ items: s.items.map((it) => (it.id === id ? { ...it, ...p } : it)) }));

  const uploadOne = async (conversationId: string, client: FootageUploadClient, item: FootageItem) => {
    if (item.file) {
      const controller = new AbortController();
      controllers.set(item.id, controller);
      patch(item.id, { status: 'uploading', pct: 0 });
      const { sourceId } = await client.uploadFile({
        conversationId,
        file: item.file,
        signal: controller.signal,
        onProgress: ({ sentBytes, totalBytes }) =>
          patch(item.id, { pct: totalBytes ? Math.min(100, (sentBytes / totalBytes) * 100) : 0 }),
      });
      controllers.delete(item.id);
      patch(item.id, { status: 'done', pct: 100, sourceId, file: undefined });
    } else if (item.url && item.linkKind && client.importLink) {
      patch(item.id, { status: 'importing' });
      const { sourceId } = await client.importLink({ conversationId, url: item.url, kind: item.linkKind });
      patch(item.id, { status: 'done', pct: 100, sourceId });
    }
  };

  return {
    items: [],

    addFiles(files) {
      const added = files.map((file): FootageItem => {
        const tooBig = assetLimitMessage(file.name, file.type, file.size);
        return tooBig
          ? { id: nextId(), name: file.name, size: file.size, status: 'error', pct: 0, error: tooBig }
          : { id: nextId(), name: file.name, size: file.size, file, status: 'staged', pct: 0 };
      });
      set((s) => ({ items: [...s.items, ...added] }));
    },

    syncLinks(links) {
      set((s) => {
        const wanted = new Set(links.map((l) => l.url));
        const kept = s.items.filter((it) => !it.url || it.status !== 'staged' || wanted.has(it.url));
        const have = new Set(kept.map((it) => it.url));
        const added = links
          .filter((l) => !have.has(l.url))
          .map((l) => ({ id: nextId(), name: footageLinkLabel(l.url, l.kind), url: l.url, linkKind: l.kind, status: 'staged' as const, pct: 0 }));
        return { items: [...kept, ...added] };
      });
    },

    remove(id) {
      controllers.get(id)?.abort();
      controllers.delete(id);
      set((s) => ({ items: s.items.filter((it) => it.id !== id) }));
    },

    reset() {
      controllers.forEach((c) => c.abort());
      controllers.clear();
      set({ items: [] });
    },

    pruneFinished() {
      set((s) => ({ items: s.items.filter((it) => it.status === 'uploading' || it.status === 'importing') }));
    },

    async start(conversationId, client) {
      if (!client.importLink) set((s) => ({ items: s.items.filter((it) => !(it.url && it.status === 'staged')) }));
      const staged = get().items.filter((it) => it.status === 'staged');
      await Promise.all(
        staged.map((item) =>
          uploadOne(conversationId, client, item).catch((err) => {
            controllers.delete(item.id);
            patch(item.id, { status: 'error', error: errorText(err, item.name) });
          }),
        ),
      );
    },
  };
});

/**
 * A file the next message waits for. The agent copies what's in storage once, when its turn starts, so a file still
 * landing is out of its reach for that whole turn. Video and audio have their own step for waiting, and can take hours.
 */
export function holdsTheMessage(it: FootageItem): boolean {
  if (it.url || (it.status !== 'staged' && it.status !== 'uploading')) return false;
  const kind = assetKind(it.name, it.file?.type);
  return kind !== 'video' && kind !== 'audio';
}

/** Starts every upload, and resolves once the files the message waits for have landed. Throws if one of them fails. */
export async function startAndHoldMessage(conversationId: string, client: FootageUploadClient): Promise<void> {
  const { getState, subscribe } = useFootageIntakeStore;
  const held = new Set(getState().items.filter(holdsTheMessage).map((it) => it.id));
  void getState().start(conversationId, client);
  await new Promise<void>((resolve) => {
    const check = () => {
      if (getState().items.some((it) => held.has(it.id) && holdsTheMessage(it))) return;
      unsubscribe();
      resolve();
    };
    const unsubscribe = subscribe(check);
    check();
  });
  const failed = getState().items.find((it) => held.has(it.id) && it.status === 'error');
  if (failed) throw new Error(failed.error);
}

/** The tag a message carries for files sent with it. A file it waited for has landed by then, so only video and audio are still uploading. */
export function uploadingFilesContext(items: FootageItem[]): string | null {
  const files = items.filter((it) => !it.url && (it.status === 'staged' || it.status === 'uploading'));
  if (!files.length) return null;
  return `[attachments: ${files.map((it) => `${it.name} (${holdsTheMessage(it) ? '' : 'uploading, '}${formatFileSize(it.size ?? 0)})`).join(', ')}]`;
}
